import React, { useState, useEffect } from 'react';
import { Button, Timeline, Tag, Spin, Empty, Modal, Typography } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  MinusCircleOutlined,
  HistoryOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from '../../api';

const { Text, Paragraph } = Typography;

/**
 * 通知历史组件 - 显示课程的通知发送历史
 */
const NotificationHistory = ({ scheduleId }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (visible && scheduleId) {
      fetchLogs();
    }
  }, [visible, scheduleId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/schedules/${scheduleId}/notification-logs/`);
      if (res && res.data) {
        setLogs(res.data || []);
      }
    } catch (error) {
      console.error('获取通知历史失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 渲染状态图标和标签
  const renderStatus = (status, statusDisplay) => {
    if (status === 'success') {
      return <Tag icon={<CheckCircleOutlined />} color="success">{statusDisplay}</Tag>;
    } else if (status === 'failed') {
      return <Tag icon={<CloseCircleOutlined />} color="error">{statusDisplay}</Tag>;
    } else {
      return <Tag icon={<MinusCircleOutlined />} color="default">{statusDisplay}</Tag>;
    }
  };

  // 渲染单条日志
  const renderLogItem = (log) => {
    const hasGoogleCalendar = log.google_calendar_status !== 'skipped';
    const hasWecom = log.wecom_status !== 'skipped';
    const hasWhatsApp = log.whatsapp_status !== 'skipped';
    const hasEmail = log.email_status !== 'skipped';

    return (
      <div key={log.id} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <Tag color="blue">{log.action_display}</Tag>
          {log.created_by_info && (
            <Text type="secondary">
              {log.created_by_info.real_name || log.created_by_info.username}
            </Text>
          )}
          <Text type="secondary" style={{ marginLeft: 8 }}>
            {dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss')}
          </Text>
        </div>

        <div style={{ paddingLeft: 16, borderLeft: '2px solid #f0f0f0' }}>
          {/* Google Calendar */}
          {hasGoogleCalendar && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>📅 Google 日历：</strong>
                {renderStatus(log.google_calendar_status, log.google_calendar_status_display)}
              </div>
              {log.google_calendar_message && (
                <Paragraph 
                  type="secondary" 
                  style={{ marginBottom: 0, fontSize: 12, paddingLeft: 16 }}
                  ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                >
                  {log.google_calendar_message}
                </Paragraph>
              )}
            </div>
          )}

          {/* WeChat */}
          {hasWecom && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>💬 企业微信：</strong>
                {renderStatus(log.wecom_status, log.wecom_status_display)}
                {log.wecom_total_count > 0 && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({log.wecom_success_count}/{log.wecom_total_count})
                  </Text>
                )}
              </div>
              {log.wecom_message && (
                <Paragraph 
                  type="secondary" 
                  style={{ marginBottom: 0, fontSize: 12, paddingLeft: 16 }}
                  ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                >
                  {log.wecom_message}
                </Paragraph>
              )}
            </div>
          )}

          {/* WhatsApp */}
          {hasWhatsApp && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>📱 WhatsApp：</strong>
                {renderStatus(log.whatsapp_status, log.whatsapp_status_display)}
                {log.whatsapp_total_count > 0 && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({log.whatsapp_success_count}/{log.whatsapp_total_count})
                  </Text>
                )}
              </div>
              {log.whatsapp_message && (
                <Paragraph 
                  type="secondary" 
                  style={{ marginBottom: 0, fontSize: 12, paddingLeft: 16 }}
                  ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                >
                  {log.whatsapp_message}
                </Paragraph>
              )}
            </div>
          )}

          {/* Email */}
          {hasEmail && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>✉️ 邮件：</strong>
                {renderStatus(log.email_status, log.email_status_display)}
                {log.email_total_count > 0 && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({log.email_success_count}/{log.email_total_count})
                  </Text>
                )}
              </div>
              {log.email_message && (
                <Paragraph 
                  type="secondary" 
                  style={{ marginBottom: 0, fontSize: 12, paddingLeft: 16 }}
                  ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                >
                  {log.email_message}
                </Paragraph>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Button 
        type="link" 
        icon={<HistoryOutlined />} 
        size="small"
        onClick={() => setVisible(true)}
      >
        查看历史
      </Button>

      <Modal
        title="通知发送历史"
        open={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        <Spin spinning={loading}>
          {logs.length === 0 ? (
            <Empty description="暂无通知发送记录" />
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {logs.map(renderLogItem)}
            </div>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default NotificationHistory;
